// @ts-strict-ignore
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Box,
  Button,
  ButtonVariant,
  Card,
  Spinner,
  Text,
  Token,
  useSnackBar,
} from '@usealloy/component-library';
import type {
  UseFormSetError,
  FieldValues,
} from 'react-hook-form';
import {
  useForm,
  FormProvider,
} from 'react-hook-form';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import log from '~/helpers/logging';
import { routes } from '~/helpers/routes';
import FormContainer from '../FormContainer';
import { InputFieldType } from '../FormContainer/types';
import { cancelExistingVerification, VERIFICATION_ID_KEY } from '../../helpers';
import { SPA_PREFIX } from '~/dashboard/constants';
import { GRAY } from '~/constants';
import {
  useNavigate, Link, useLocation, useParams,
} from 'react-router-dom';

interface IFormFields {
  phoneNumber: string;
}

const formDefaultValues = {
  phoneNumber: '',
};

const MFA_BANNER_TEXT = 'Multi-Factor Authentication (MFA) adds an extra layer of security to your account. Your administrator requires using MFA in addition to your password to sign in to Alloy';

export const MfaSetupViewWithTotp = () => {
  const { tokenUid, token } = useParams();

  const [qrCode, setQrCode] = useState('');

  const [authCode, setAuthCode] = useState('');

  const [loading, setLoading] = useState(false);

  const addSnackBar = useSnackBar();

  const endpointUrl = routes.authentication.mfa.verifyNumber(tokenUid, token);

  const setupTotp = buildURLParamsString({
    totp: true,
  });

  useEffect(() => {
    const getqrCodeAndAuthCode = async () => {
      setLoading(true);

      try {
        const response = await ajaxFetch(`${endpointUrl}${setupTotp}`, {
          method: 'GET',
          camelCase: true,
          enhancedErrorResponse: true,
        });

        const {
          qrCode: qrCodeResposne,
          authCode: authCodeResponse,
          verification,
        } = response;

        localStorage.setItem('totpSid', verification?.sid);
        setQrCode(qrCodeResposne);
        setAuthCode(authCodeResponse);
        setLoading(false);
      } catch (e) {
        addSnackBar({
          title: 'Error fetching QR code.',
          type: 'danger',
        });

        setLoading(false);
      }
    };

    const TEN_MINUTES = 1000 * 60 * 10;

    getqrCodeAndAuthCode();

    // QR code expires every to minutes so we need to refresh every time
    const intervalId = setInterval(() => {
      addSnackBar({
        title: 'QR code has been re-fetched after expiring. Please re-scan',
        type: 'danger',
      });
      getqrCodeAndAuthCode();
    }, TEN_MINUTES);

    return () => clearInterval(intervalId);
  }, [endpointUrl, setupTotp, addSnackBar]);

  return (
    <Box
      direction="column"
      gap={4}
      alignItems="stretch"
      style={{ width: '741px' }}
    >
      {/* HEADER */}
      <Text as="h1" size={5} style={{ textAlign: 'center' }}>
        Multi-Factor Authentication for Alloy
      </Text>

      {/* INFO BANNER */}
      <Banner variant="info" outline borderColor="var(--cl-juicyBlue-400)" open>
        <Text>{MFA_BANNER_TEXT}</Text>
      </Banner>

      {/* QR CONTAINER */}
      <Card
        header="Scan the QR code below using your Authenticator app"
        headerPosition="center"
        showContentSeparator={false}
        style={{ borderRadius: '8px' }}
      >
        <Box gap={6} direction="column">
          {loading ? (
            <div
              role="region"
              aria-label="qr-code-placeholder"
              style={{
                width: '200px',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spinner size="large" />
            </div>
          ) : qrCode ? (
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              style={{ width: '200px', height: '200px' }}
            />
          ) : (
            <Text>Unable to display QR code for TOTP setup</Text>
          )}
          <Box gap={3} direction="column">
            <Text
              style={{ width: '320px', textAlign: 'center', fontSize: '12px' }}
            >
              Can&apos;t scan the QR code? Enter the following code into the
              authenticator app to link it to your Alloy account.
            </Text>
            {loading ? (
              <Token displayContent="Please wait, loading..." />
            ) : authCode ? (
              <Token displayContent={authCode} />
            ) : (
              <Text>Unable to display auth code</Text>
            )}
          </Box>
          <Link
            to={`${SPA_PREFIX}/mfa/totp/${tokenUid}/${token}`}
            state={{ from: 'mfa-setup-totp' }}
          >
            <Button variant={ButtonVariant.primary}>Continue</Button>
          </Link>
        </Box>
      </Card>

      {/* SMS PROMPT */}
      <Box direction="column" justifyContent="center" gap={4}>
        <Text size={3}>
          Unable to use an Authenticator app?
          <Link
            to={`${SPA_PREFIX}/mfa/verify/${tokenUid}/${token}`}
            style={{ textDecorationColor: GRAY.gray1100 }}
          >
            <Text
              style={{
                color: GRAY.gray1100,
                display: 'inline',
                fontSize: '16px',
              }}
            >
              Verify your phone number via text message
            </Text>
          </Link>
        </Text>

        <Text style={{ fontSize: '12px' }}>
          *This option is only available for users with a US mobile number.
        </Text>
      </Box>
    </Box>
  );
};

const MFASetupView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tokenUid, token } = useParams();
  const addSnackBar = useSnackBar();

  const methods = useForm<IFormFields>({
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  const fields = [
    {
      type: InputFieldType.TEL,
      label: 'Verify Phone Number',
      name: 'phoneNumber',
      error: 'Make sure supplied phone number is valid (e.g. 000-000-0000)',
      pattern: /^[(]?[0-9]{3}[)]?[-][0-9]{3}[-][0-9]{4}$/,
      placeholder: 'e.g. 000-000-0000',
      required: true,
    },
  ];

  const nextUrl = useMemo(() => location?.state?.nextUrl || '/', [location]);

  const onPhoneNumberVerifySubmit = async (
    formData: FieldValues,
    onSubmitOptions: { setError: UseFormSetError<FieldValues> },
  ) => {
    const genericErrorMessage = 'Something went wrong while verifying the phone number';
    try {
      await cancelExistingVerification();
      const endpointUrl = routes.authentication.mfa.verifyNumber(
        tokenUid,
        token,
      );
      const verifyNumberParams = buildURLParamsString({
        sms: true,
        phone: formData.phoneNumber,
      });
      const response = await ajaxFetch(`${endpointUrl}${verifyNumberParams}`, {
        method: 'GET',
        camelCase: true,
        enhancedErrorResponse: true,
      });

      if (response?.success === true) {
        if (response.verification) {
          // Record the SID of the verification in localStorage if we need to cancel and create
          // a new one
          window.localStorage.setItem(
            VERIFICATION_ID_KEY,
            response.verification.sid,
          );
        }

        navigate(
          routes.v3.dashboard.authentication['mfa-verify-code'](`${tokenUid}/${token}`),
          {
            state: {
              nextUrl,
              from: 'mfa-setup-verify-phonenumber',
              phoneNumber: formData.phoneNumber,
            },
          },
        );
      }

      if (response?.success === false) {
        addSnackBar({
          title: response.message || genericErrorMessage,
          type: 'danger',
        });

        onSubmitOptions.setError('phoneNumber', {
          type: 'manual',
          message: response.message,
        });
      }
    } catch (err) {
      addSnackBar({
        title: err.message || genericErrorMessage,
        type: 'danger',
      });
      log(
        'error',
        `GET ${routes.authentication.mfa.verifyNumber(
          tokenUid,
          token,
        )} exception`,
        {},
        err,
      );
    }
  };

  React.useEffect(() => {
    methods.setFocus('phoneNumber');
  }, [methods]);

  return (
    <Box
      direction="column"
      gap={8}
      alignItems="stretch"
      style={{ width: '480px' }}
    >
      <Text as="h1" style={{ textAlign: 'center', fontSize: '64px' }}>
        Verify Login
      </Text>
      <p style={{ textAlign: 'center', fontSize: '16px', color: 'black' }}>
        Multi-factor authentication has been required for this account.
        &nbsp;Please provide a phone number for your account.
      </p>
      <FormProvider {...methods}>
        <FormContainer
          centerSubmit
          fields={fields}
          onSubmit={(formData, options) => onPhoneNumberVerifySubmit(formData, options)}
          onSubmitActionText="Verify Phone Number"
          submitButtonVariant={ButtonVariant.primary}
          warning="By providing your phone number you consent to receive a text message. Message and data rates may apply."
        />
      </FormProvider>
    </Box>
  );
};

export default MFASetupView;
